import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { SlidersHorizontal } from "lucide-react";

const DOSHAS = [
  { slug: "vata", label: "Vata (Air)" },
  { slug: "pitta", label: "Pitta (Fire)" },
  { slug: "kapha", label: "Kapha (Earth)" },
  { slug: "tridoshic", label: "Tridoshic" },
];

export default function Products() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = params.get("category") || "";
  const dosha = params.get("dosha") || "";
  const q = params.get("q") || "";
  const sort = params.get("sort") || "featured";

  useEffect(() => {
    http.get("/categories").then((r) => setCategories(r.data));
  }, []);

  useEffect(() => {
    setLoading(true);
    http
      .get("/products", { params: { category: category || undefined, dosha: dosha || undefined, q: q || undefined } })
      .then((r) => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [category, dosha, q]);

  const sorted = useMemo(() => {
    const list = [...products];
    if (sort === "price-asc") list.sort((a, b) => a.price - b.price);
    else if (sort === "price-desc") list.sort((a, b) => b.price - a.price);
    else if (sort === "rating") list.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    return list;
  }, [products, sort]);

  const set = (key, val) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val); else next.delete(key);
    setParams(next);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="mb-8">
        <div className="text-xs font-mono text-[#D97706] uppercase tracking-wider">Marketplace</div>
        <h1 className="font-serif text-4xl font-bold text-[#1A201C] mt-1">
          {q ? `Results for "${q}"` : category ? categories.find((c) => c.slug === category)?.name || "Products" : "All Ayurvedic Products"}
        </h1>
        <p className="text-[#5A6B5D] mt-1 text-sm">{sorted.length} verified products from Indian artisan brands</p>
      </div>

      <div className="grid gap-8 lg:grid-cols-[240px_1fr]">
        <aside className="space-y-6" data-testid="filters-sidebar">
          <div className="flex items-center gap-2 font-serif text-lg font-semibold text-[#1E3A2B]">
            <SlidersHorizontal className="w-4 h-4" /> Filters
          </div>

          <div>
            <div className="text-xs font-mono uppercase text-[#5A6B5D] mb-2">Category</div>
            <div className="space-y-1.5">
              <button onClick={() => set("category", "")} className={`block text-sm text-left w-full py-1 ${!category ? "text-[#1E3A2B] font-semibold" : "text-[#1A201C]"}`}>All Categories</button>
              {categories.map((c) => (
                <button
                  key={c.slug}
                  onClick={() => set("category", c.slug)}
                  data-testid={`filter-category-${c.slug}`}
                  className={`block text-sm text-left w-full py-1 ${category === c.slug ? "text-[#1E3A2B] font-semibold" : "text-[#1A201C]"}`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs font-mono uppercase text-[#5A6B5D] mb-2">Dosha</div>
            <div className="flex flex-wrap gap-2">
              {DOSHAS.map((d) => (
                <button
                  key={d.slug}
                  onClick={() => set("dosha", dosha === d.slug ? "" : d.slug)}
                  data-testid={`filter-dosha-${d.slug}`}
                  className={`text-xs px-3 py-1.5 rounded-full border ${dosha === d.slug ? "bg-[#1E3A2B] text-white border-[#1E3A2B]" : "bg-white border-[#E2DDD5] text-[#1A201C]"}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-xs font-mono uppercase text-[#5A6B5D] mb-2">Sort by</div>
            <select
              value={sort}
              onChange={(e) => set("sort", e.target.value === "featured" ? "" : e.target.value)}
              className="w-full border border-[#E2DDD5] bg-white rounded-lg px-3 py-2 text-sm"
              data-testid="sort-select"
            >
              <option value="featured">Featured</option>
              <option value="price-asc">Price: Low to High</option>
              <option value="price-desc">Price: High to Low</option>
              <option value="rating">Top Rated</option>
            </select>
          </div>
        </aside>

        <div>
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => <div key={i} className="aspect-[3/4] bg-[#E8F0EA] rounded-2xl animate-pulse" />)}
            </div>
          ) : sorted.length === 0 ? (
            <div className="text-center py-20 text-[#5A6B5D]">No products match your filters.</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {sorted.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
